import React, { Children, isValidElement, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { progressStepComponentHeight } from "components/basic/progressSteps";
import ScrollContainer from "components/basic/scrollContainer";
import FormPageActions from "./components/FormPageActions";
import FormPageImage from "./components/FormPageImage";
import useOffsetTop from "hooks/useOffsetTop";
import useRevealNextField from "./helpers/useRevealNextField";
import { FormPageAction } from "./types";

export type { FormPageAction } from "./types";

export interface FormPageLayoutProps {
  title?: React.ReactNode;
  children: React.ReactNode;
  imageSrc?: string;
  imageAlt?: string;
  imagePosition?: "left" | "right";
  contentTextAlign?: React.CSSProperties["textAlign"];
  // Where the form column sits horizontally within its half.
  contentAlign?: "center" | "end";
  contentMaxWidth?: number;
  bodyScroll?: boolean;
  // Leave room for the absolutely positioned ProgressSteps bar.
  reserveProgressBar?: boolean;
  // Centre the form vertically while it fits, scrolling only once it doesn't.
  centerVertically?: boolean;
  backAction?: FormPageAction;
  primaryAction?: FormPageAction;
}

const FormPageLayout: React.FC<FormPageLayoutProps> = ({
  title,
  children,
  imageSrc,
  imageAlt,
  imagePosition = "right",
  contentTextAlign = "center",
  contentAlign = "center",
  contentMaxWidth = 360,
  bodyScroll = true,
  reserveProgressBar = true,
  centerVertically = false,
  backAction,
  primaryAction,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  // Below md the page flows and scrolls normally, so the inner scroll region is
  // skipped entirely rather than nesting a scroller inside a fixed-height page.
  const isCompact = useMediaQuery(theme.breakpoints.down("md"));

  // Whatever sits above the form varies: the marketplace header gains a row at
  // narrower widths (51px wide, 61px below ~1100px) and the vendor portal has no
  // header at all. Measuring it beats any constant, which was overflowing the
  // page by exactly the difference.
  const rootRef = useRef<HTMLDivElement>(null);
  const offsetTop = useOffsetTop(rootRef);

  const { fieldsRef, handleFieldInteraction } = useRevealNextField();

  const pageHeight = `calc(100dvh - ${offsetTop}px)`;
  const contentHeight = reserveProgressBar
    ? `calc(100% - ${progressStepComponentHeight}px)`
    : "100%";
  const scrollContainerMaxWidth = contentMaxWidth + 80;

  const image = imageSrc ? (
    <FormPageImage src={imageSrc} alt={imageAlt ?? t("ui.image_alt")} />
  ) : null;

  const body = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
        ...(bodyScroll
          ? {
              width: "100%",
              maxWidth: scrollContainerMaxWidth,
              paddingRight: { xs: 0, md: theme.spacing(2) },
              boxSizing: "border-box",
              scrollbarWidth: "thin",
            }
          : {}),
      }}
    >
      <Box
        ref={fieldsRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing(3),
          width: "100%",
          maxWidth: contentMaxWidth,
          alignSelf: "center",
          margin: "0 auto",
          scrollbarWidth: "thin",
        }}
      >
        {/* toArray drops `cond && <Field />` gaps, which would otherwise
            become empty wrappers that still take up a flex gap. */}
        {Children.toArray(children).map((child, index) => (
          <div
            key={
              isValidElement(child) && child.key !== null ? child.key : index
            }
            onClickCapture={(event) => handleFieldInteraction(event, index)}
            onKeyDownCapture={(event) => handleFieldInteraction(event, index)}
          >
            {child}
          </div>
        ))}
      </Box>
    </Box>
  );

  return (
    <Box
      ref={rootRef}
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: { xs: "auto", md: pageHeight },
      }}
    >
      {imagePosition === "left" ? image : null}

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: contentAlign === "end" ? "flex-end" : "center",
          alignItems: { xs: "flex-start", md: "center" },
          height: { xs: "auto", md: contentHeight },
          textAlign: contentTextAlign,
          minHeight: 0,
          paddingX: { xs: 2, sm: 3, md: 4 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
            width: "100%",
            maxWidth: bodyScroll ? scrollContainerMaxWidth : contentMaxWidth,
            // Fill the pane exactly, so the scroll region below can absorb the
            // leftover space rather than the column overflowing and being centred
            // out of view at both ends. When centring, cap it instead so a short
            // form keeps its natural height.
            height: { xs: "auto", md: centerVertically ? "auto" : "100%" },
            maxHeight: { md: "100%" },
            minHeight: 0,
            paddingTop: title
              ? 0
              : { xs: theme.spacing(3), md: theme.spacing(4) },
            boxSizing: "border-box",
          }}
        >
          {title ? (
            <Box
              sx={{
                flexShrink: 0,
                paddingTop: { xs: theme.spacing(3), md: theme.spacing(6) },
                width: "100%",
                maxWidth: contentMaxWidth,
                alignSelf: "center",
              }}
            >
              {title}
            </Box>
          ) : null}

          {isCompact ? (
            body
          ) : (
            <Box
              sx={{
                flex: centerVertically ? "0 1 auto" : 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                // Deliberately not `center`: on the column axis that shrinks the
                // scroll region to its content width and squeezes the fields.
                alignItems: "stretch",
              }}
            >
              <ScrollContainer
                height={centerVertically ? "auto" : "100%"}
                sx={centerVertically ? { flex: "0 1 auto" } : {}}
              >
                {body}
              </ScrollContainer>
            </Box>
          )}

          <FormPageActions
            backAction={backAction}
            primaryAction={primaryAction}
            maxWidth={contentMaxWidth}
          />
        </Box>
      </Box>

      {imagePosition === "right" ? image : null}
    </Box>
  );
};

export default FormPageLayout;
